import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import styled from 'styled-components';
import TabPanel from '../../components/TabPanel';
import OrdersTab from './tabs/OrdersTab';
import SettingsTab from './tabs/SettingsTab';
import SalesHistoryTab from './tabs/SalesHistoryTab';
import { orderService, storeService } from '../../data';
import { useAuth } from '../../App';
import CircleKLogo from '../../assets/Circle-K-Logo.png';
import { FaMapMarkerAlt, FaPhone } from 'react-icons/fa';

const TABS = {
  ORDERS: 'orders',
  SETTINGS: 'settings',
  HISTORY: 'history'
};

const Container = styled.div`
  padding: 2rem;
  max-width: 1400px;
  margin: 0 auto;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;

  @media (max-width: 768px) {
    padding: 1rem;
  }
`;

const StoreInfo = styled.div`
  background: white;
  padding: 2rem;
  border-radius: 12px;
  margin-bottom: 2rem;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);

  @media (max-width: 768px) {
    padding: 1.5rem;
    margin-bottom: 1.5rem;
  }
`;

const HeaderSection = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 2rem;

  @media (max-width: 1024px) {
    gap: 1.5rem;
  }

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 1rem;
  }
`;

const StoreName = styled.h1`
  color: #4A3C31;
  display: flex;
  align-items: center;
  gap: 1.5rem;
  font-size: 2.2rem;
  font-weight: 700;
  letter-spacing: -0.5px;
  font-family: 'Poppins', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  margin: 0;

  @media (max-width: 1024px) {
    font-size: 1.8rem;
    gap: 1rem;
  }

  @media (max-width: 768px) {
    font-size: 1.5rem;
    gap: 0.75rem;
    flex-wrap: wrap;
  }
`;

const Logo = styled.img`
  height: 50px;
  margin-right: 1rem;
  filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.1));

  @media (max-width: 1024px) {
    height: 40px;
    margin-right: 0.75rem;
  }

  @media (max-width: 768px) {
    height: 35px;
    margin-right: 0.5rem;
  }
`;

const StoreDetails = styled.div`
  display: flex;
  gap: 2rem;
  color: #2C1810;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;

  @media (max-width: 1024px) {
    gap: 1.5rem;
  }

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 0.75rem;
    width: 100%;
  }
`;

const DetailItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;

  @media (max-width: 768px) {
    gap: 0.5rem;
    align-items: flex-start;
  }
`;

const IconWrapper = styled.div`
  color: #4A3C31;
  font-size: 1.1rem;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;

  @media (max-width: 768px) {
    font-size: 1rem;
    width: 20px;
    margin-top: 0.1rem;
  }
`;

const DetailValue = styled.span`
  color: #2C1810;
  font-size: 0.9rem;
  line-height: 1.4;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;

  div {
    margin-bottom: 0.25rem;
  }

  @media (max-width: 768px) {
    font-size: 0.85rem;
    line-height: 1.3;

    div {
      margin-bottom: 0.2rem;
    }
  }
`;

const formatPhoneNumber = (phoneNumber) => {
  // Remove all non-digit characters
  const cleaned = phoneNumber.replace(/\D/g, '');
  
  // Check if we have a valid 10-digit number
  if (cleaned.length === 10) {
    // Format as (xxx)xxx-xxxx
    return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
  }
  
  // Return original if not a valid 10-digit number
  return phoneNumber;
};

const Store = () => {
  const location = useLocation();
  const { user } = useAuth();
  const stores = useMemo(() => user && user.stores ? user.stores : [], [user]);
  const [store, setStore] = useState(null);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState(() => {
    const params = new URLSearchParams(location.search);
    const savedTab = params.get('tab') || localStorage.getItem('storeActiveTab') || TABS.ORDERS;
    return savedTab;
  });
  const loadingRef = useRef(false);

  // Load store details from API
  const loadStoreDetails = async (storeId) => {
    try {
      setLoading(true);
      console.log('Loading store details for ID:', storeId);
      
      const storeDetails = await storeService.getStoreDetails(storeId);
      console.log('Received store details from API:', storeDetails);
      
      if (storeDetails) {
        const updatedStore = {
          ...storeDetails,
          customer_id: storeId,
          market: storeDetails.market // Use market from store details instead of current store state
        };
        console.log('Setting store with details:', updatedStore);
        setStore(updatedStore);
      } else {
        console.error('No store details received');
        setError('Failed to load store details');
      }
    } catch (err) {
      console.error('Failed to load store details:', err);
      setError('Failed to load store details');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const storeId = location.pathname.split('/').pop();
    const market = params.get('market');
    
    console.log('Store effect triggered with:', { storeId, market, state: location.state });
    
    if (storeId && market) {
      // If we have state data, merge it with URL parameters
      if (location.state?.store) {
        console.log('Using store from state:', location.state.store);
        setStore({
          ...location.state.store,
          customer_id: storeId,
          market: market
        });
      } else {
        console.log('No store in state, checking auth context');
        // First try to get the store from the stores list in auth context
        const storeFromList = stores?.find(s => s.customer_id === parseInt(storeId));
        
        if (storeFromList) {
          console.log('Found store in auth context:', storeFromList);
          setStore({
            ...storeFromList,
            customer_id: storeId,
            market: market
          });
        } else {
          console.log('Store not found in auth context, loading from API');
          // If no state data, ensure URL parameters are preserved
          const updatedParams = new URLSearchParams();
          updatedParams.set('market', market);
          if (params.has('tab')) {
            updatedParams.set('tab', params.get('tab'));
          }
          const newUrl = `/store/${storeId}?${updatedParams.toString()}`;
          
          // Only replace state if URL would change
          if (window.location.pathname + window.location.search !== newUrl) {
            window.history.replaceState(null, '', newUrl);
          }
          
          // Set initial store state and load details
          setStore({
            customer_id: storeId,
            market: market
          });
          loadStoreDetails(storeId);
        }
      }
    }
  }, [location, stores]);

  const loadOrders = useCallback(async () => {
    if (!store?.customer_id || !store?.market || loadingRef.current) return;
    loadingRef.current = true;

    try {
      setLoading(true);
      setError(null);
      const response = await orderService.getOrders(store.customer_id, store.market);
      setOrders(response.items);
    } catch (err) {
      console.error('Failed to load orders:', err);
      setError('Failed to load orders. Please try again later.');
    } finally {
      setLoading(false);
      loadingRef.current = false;
    }
  }, [store?.customer_id, store?.market]);

  // Save active tab when it changes
  useEffect(() => {
    localStorage.setItem('storeActiveTab', activeTab);
    // Update URL with current tab
    const params = new URLSearchParams(location.search);
    params.set('tab', activeTab);
    const newUrl = `${location.pathname}?${params.toString()}`;
    window.history.replaceState(null, '', newUrl);
  }, [activeTab, location.pathname, location.search]);

  useEffect(() => {
    loadOrders();
  }, [store?.customer_id, store?.market, loadOrders]);

  const tabs = store ? [
    {
      id: TABS.ORDERS,
      label: 'Billing',
      content: <OrdersTab 
        market={store.market}
        customerId={store.customer_id}
        orders={orders}
        loading={loading}
        error={error}
      />
    },
    {
      id: TABS.SETTINGS,
      label: 'Ordering',
      content: <SettingsTab 
        market={store.market}
        customerId={store.customer_id}
      />
    },
    {
      id: TABS.HISTORY,
      label: 'Order History',
      content: <SalesHistoryTab 
        market={store.market}
        customerId={store.customer_id}
        storeName={store.name}
      />
    }
  ] : [];

  if (!store) {
    return <Container>Store not found</Container>;
  }

  return (
    <Container>
      <StoreInfo>
        <HeaderSection>
          <StoreName>
            <Logo src={CircleKLogo} alt="Circle K Logo" />
            {store.name}
          </StoreName>
          <StoreDetails>
            <DetailItem>
              <IconWrapper>
                <FaMapMarkerAlt />
              </IconWrapper>
              <DetailValue>
                <div>{store.address1}</div>
                {store.address2 && <div>{store.address2}</div>}
                <div>{store.city}, {store.state} {store.zip_code}</div>
              </DetailValue>
            </DetailItem>
            <DetailItem>
              <IconWrapper>
                <FaPhone />
              </IconWrapper>
              <DetailValue>{formatPhoneNumber(store.phone)}</DetailValue>
            </DetailItem>          
          </StoreDetails>
        </HeaderSection>
      </StoreInfo>
      <TabPanel
        tabs={tabs}
        activeTab={activeTab}
        onTabChange={setActiveTab}
      />
    </Container>
  );
};

export default Store;
